import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonOption } from '../../Fundamentals/Button';
import Drawer from '../../Fundamentals/Drawer/Drawer';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Fundamentals/Spinner/Spinner';
import PackageAudienceFields from './PackageAudienceFields';
import PackageBasicsFields from './PackageBasicsFields';
import PackageFlagToggles from './PackageFlagToggles';
import { isFullyEditable } from './packageConstants';
import { hasPackageDraftErrors, packageDraftErrors, toPackageDraft, toUpdatePackageRequest } from './packageDraftUtils';
import type { PackageDetail, PackageDraft, TargetGroupOption, UpdatePackageRequest } from './Packages.types';

interface PackageEditDrawerProps {
    vossPackage: PackageDetail;
    targetGroupOptions: TargetGroupOption[];
    onClose: () => void;
    onSave: (model: UpdatePackageRequest) => void;
    isSaving: boolean;
}

const PackageEditDrawer = ({ vossPackage, targetGroupOptions, onClose, onSave, isSaving }: PackageEditDrawerProps) => {
    const { t } = useTranslation();

    const [draft, setDraft] = useState(() => toPackageDraft(vossPackage));
    const update = (change: Partial<PackageDraft>) => setDraft((prev) => ({ ...prev, ...change }));

    const canEditAll = isFullyEditable(vossPackage.status);
    const errors = packageDraftErrors(draft);

    return (
        <Drawer
            title={t('packages.editPackage') ?? ''}
            subtitle={t('packages.editPackageSubtitle') ?? ''}
            onClose={onClose}
            canClose={!isSaving}
            footer={
                <>
                    {isSaving && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
                    <Button disabled={isSaving} onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button option={ButtonOption.PRIMARY} disabled={isSaving || hasPackageDraftErrors(errors)} onClick={() => onSave(toUpdatePackageRequest(draft, vossPackage))}>
                        {t('common.save')}
                    </Button>
                </>
            }
        >
            {!canEditAll && <div className="alert alert-info mb-16">{t('packages.limitedEditing')}</div>}
            <h4 className="font-16">{t('packages.basicDetails')}</h4>
            <PackageBasicsFields draft={draft} errors={errors} locked={!canEditAll} onChange={update} />

            <hr />
            <PackageAudienceFields
                audiences={draft.audiences}
                targetGroupIds={draft.targetGroupIds}
                targetGroupOptions={targetGroupOptions}
                audiencesLocked={!canEditAll}
                onChange={update}
            />

            <div className="border-bottom my-24" />
            <PackageFlagToggles flags={draft.flags} disabled={!canEditAll} onChange={(flags) => update({ flags })} />
        </Drawer>
    );
};

export default PackageEditDrawer;
